/**
 * Utility functions for sharing registry links across different platforms
 */

/**
 * Share registry link on Facebook
 * Copies the link to clipboard and opens Facebook share dialog
 */
export const shareOnFacebook = (registryUrl: string) => {
  // First copy to clipboard
  navigator.clipboard.writeText(`I just created a MommaFuel for my family! Visit it here: ${registryUrl}`).then(() => {
    // Then open Facebook
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(registryUrl)}&quote=Check%20out%20this%20amazing%20website%21`, '_blank');
  }).catch(err => {
    console.error('Could not copy text: ', err);
    // Still open Facebook even if copy fails
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(registryUrl)}&quote=Check%20out%20this%20amazing%20website%21`, '_blank');
  });
};

/**
 * Share registry link via Email
 * Opens default email client with pre-filled subject and body
 */
export const shareViaEmail = (registryUrl: string, familyName?: string) => {
  const subject = `MommaFuel for ${familyName || 'Our Family'}`;
  const body = `Hi! We've created a MommaFuel. You can view it here: ${registryUrl}`;
  const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  window.location.href = mailtoUrl;
};

/**
 * Share registry link via Text Message
 * Opens default SMS app with pre-filled message
 */
export const shareViaText = (registryUrl: string) => {
  const message = `Hi! We've created a MommaFuel. You can view it here: ${registryUrl}`;
  const smsUrl = `sms:?body=${encodeURIComponent(message)}`;
  window.location.href = smsUrl;
};

/**
 * Input validation function that only allows digits
 * Use with onKeyDown event handler
 */
export const onlyAllowDigits = (e: React.KeyboardEvent<HTMLInputElement>) => {
  // Allow: backspace, delete, tab, escape, enter, etc.
  if ([46, 8, 9, 27, 13].indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A
      (e.keyCode === 65 && e.ctrlKey === true) ||
      // Allow: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39)) {
    return;
  }
  // Ensure that it's a number and stop the keypress
  if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && 
      (e.keyCode < 96 || e.keyCode > 105)) {
    e.preventDefault();
  }
};

/**
 * Email validation function
 * Use with onBlur event handler
 * Returns empty string if valid, error message if invalid
 */
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Email validation handler for React forms
 * Use with onBlur event handler
 * Will clear the field if email is invalid and show an alert
 */
export const validateEmailField = (e: React.FocusEvent<HTMLInputElement>) => {
  const email = e.target.value.trim();
  if (email && !validateEmail(email)) {
    // If there's content but it's invalid, show a message
    alert('Please enter a valid email address');
    e.target.value = ''; // Clear the field
  }
};

export const validatePhone = (phone: string): boolean => {
  // Requires at least 10 digits (US phone number)
  return phone.replace(/\D/g, '').length >= 10;
}; 